/**
 * Pobieranie plików z API (PDF/ZIP/XLSX) pod nazwą z serwera — jeden kanon dla całego panelu.
 *
 * Audyt 2026-09-30: PDF-y otwierane jako `blob:` w nowej karcie zapisywały się pod losową nazwą
 * (UUID object URL-a), bo `Content-Disposition` z backendu (np.
 * `podsumowanie_<ulica>_<nr>_<RRRR-MM-DD>.pdf`) nigdy nie docierał do pliku. Teraz:
 * `filenameFromContentDisposition` czyta nazwę z nagłówka, a `saveBlob` zapisuje przez
 * `<a download>`.
 *
 * Nagłówek jest czytelny, bo panel woła API z TEGO SAMEGO originu (`/api` przez nginx). Przy
 * wywołaniu cross-origin (np. dev :8090 → :3010) przeglądarka go ukryje (backend nie ustawia
 * `Access-Control-Expose-Headers`) — wtedy zostaje nazwa zapasowa wołającego.
 */

/**
 * Nazwa pliku z `Content-Disposition`. `filename*` (RFC 5987, UTF-8) wygrywa nad `filename`.
 * Zwraca `null`, gdy nagłówka nie ma albo nie niesie nazwy.
 */
export function filenameFromContentDisposition(header: string | null | undefined): string | null {
  if (!header) return null;
  const extended = /filename\*\s*=\s*([^']*)'[^']*'([^;]+)/i.exec(header);
  if (extended) {
    const value = extended[2].trim().replace(/^"(.*)"$/, '$1');
    try {
      const decoded = decodeURIComponent(value);
      if (decoded) return stripPath(decoded);
    } catch {
      // niepoprawne %-kodowanie — spróbuj zwykłego `filename`
    }
  }
  const quoted = /filename\s*=\s*"((?:\\.|[^"\\])*)"/i.exec(header);
  if (quoted && quoted[1]) return stripPath(quoted[1].replace(/\\(.)/g, '$1'));
  const token = /filename\s*=\s*([^;\s]+)/i.exec(header);
  if (token && token[1]) return stripPath(token[1]);
  return null;
}

/** Nazwa z nagłówka nie może nieść ścieżki — przeglądarki i tak ją tną, ale nie polegamy na tym. */
function stripPath(name: string): string {
  const parts = name.split(/[\\/]/);
  return parts[parts.length - 1].trim();
}

/**
 * Nazwa pliku bez polskich znaków i spacji — `Content-Disposition` i systemy plików bywają
 * wrażliwe. Ta sama reguła co `sanitizeFilename` backendu (utils/pdf.js).
 */
export function asciiFilename(value: string): string {
  const map: Record<string, string> = {
    ą: 'a', ć: 'c', ę: 'e', ł: 'l', ń: 'n', ó: 'o', ś: 's', ź: 'z', ż: 'z',
    Ą: 'A', Ć: 'C', Ę: 'E', Ł: 'L', Ń: 'N', Ó: 'O', Ś: 'S', Ź: 'Z', Ż: 'Z',
  };
  return value
    .replace(/[ąćęłńóśźżĄĆĘŁŃÓŚŹŻ]/g, (ch) => map[ch] || ch)
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
}

/** Zapisuje Blob jako plik o podanej nazwie (a nie otwiera go w karcie). */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Zwolnij po chwili — natychmiastowe revoke potrafi przerwać start pobierania.
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

function csvCell(value: string | number): string {
  const s = String(value);
  return /[";\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/**
 * CSV dla Excela w polskich ustawieniach: separator `;`, BOM (UTF-8 z ogonkami), CRLF — jeden kanon
 * dla eksportów panelu (niesprawdzone lokale, źródła ciepła, numery protokołów).
 */
export function excelCsv(lines: (string | number)[][]): string {
  return `\ufeff${lines.map((l) => l.map(csvCell).join(';')).join('\r\n')}\r\n`;
}
